import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Coins,
  ListPlus,
  Lock,
  Vault,
  Rocket,
  Zap,
  Layers,
  ArrowRight,
  ArrowLeftRight,
  Link2,
  Star,
  Cpu,
  ShieldCheck,
  GitBranch,
  Sparkles,
  X,
  Twitter,
  Send,
  Mail,
  FileText,
  Github,
  Globe,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { DigitalBackground } from "@/components/DigitalBackground";
import { LogoWatermark } from "@/components/LogoWatermark";
import { ElectricCard } from "@/components/ElectricCard";
import { SwapDetail } from "@/components/SwapDetail";
import { TokenDetail } from "@/components/TokenDetail";
import { ListDetail } from "@/components/ListDetail";
import { LockerDetail } from "@/components/LockerDetail";
import { AirdropDetail } from "@/components/AirdropDetail";
import { ReviewsDetail } from "@/components/ReviewsDetail";
import { ReviewsMarquee } from "@/components/ReviewsMarquee";
import { ChatWall } from "@/components/ChatWall";

import { AuthModal } from "@/components/AuthModal";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const capabilities = [
  {
    icon: Coins,
    title: "Create Tokens",
    desc: "Instantly deploy ERC-20 standard or custom tax tokens with tailored supply parameters.",
    detail: "token",
  },
  {
    icon: ListPlus,
    title: "List Tokens",
    desc: "Automated DEX pool creation with optimal initial pricing algorithms.",
    detail: "list",
  },
  {
    icon: Lock,
    title: "Create Token Lockers",
    desc: "Permanently lock tokens or LP assets on-chain with verifiable public records.",
    detail: "locker",
  },
  {
    icon: Vault,
    title: "Create Staking Vaults",
    desc: "Custom yield-farming schedules and dynamic staking reward pools.",
    long: "Launch flexible or fixed staking vaults with fully customizable reward curves, lock durations and emission schedules. Reward your holders automatically while the engine handles distribution, compounding and pool accounting on-chain.",
  },
  {
    icon: Rocket,
    title: "Autonomous Airdropper",
    desc: "Auto-populate recipient wallets from on-chain data — no spreadsheets, no CSV uploads.",
    detail: "airdrop",
  },
  {
    icon: Zap,
    title: "Create & Manage Launches",
    desc: "Fair-launch launchpad mechanics with anti-bot protection built in.",
    long: "Run fair, transparent launches with built-in anti-bot and anti-snipe protection, contribution caps and automatic liquidity provisioning. Monitor the raise in real time and finalize to live trading with a single autonomous action.",
  },
  {
    icon: Layers,
    title: "Manage DeFi Ecosystem",
    desc: "Unified command console to monitor stats, burn rates, and treasury flow.",
    long: "One console for your entire protocol. Track holders, volume, burn rates, locked liquidity and treasury flow across every token and vault you've deployed — a live, unified command center for your whole DeFi ecosystem.",
  },
  {
    icon: ArrowLeftRight,
    title: "Autono Swap",
    desc: "The native swap terminal where tokens are traded instantly on-chain.",
    detail: "swap",
  },
  {
    icon: Link2,
    title: "Contacts & Links",
    desc: "Connect with the Autonomous Dex community and explore official resources.",
    long: "Stay connected to Autonomous Dex. Reach the team, join the community and access official documentation, source and announcements through the verified links below.",
    links: [
      { icon: Globe, label: "Website", href: "https://autonodex.org/" },
      { icon: Twitter, label: "X / Twitter", href: "https://x.com/autonodex" },
      { icon: Send, label: "Telegram", href: "#" },
      { icon: Mail, label: "Email", href: "#" },
      { icon: FileText, label: "Docs", href: "#" },
      { icon: Github, label: "GitHub", href: "#" },
    ],
  },
  {
    icon: Star,
    title: "Customer Reviews",
    desc: "See what the community is saying — and leave your own 1–5 star review.",
    detail: "reviews",
  },
];

const aiFeatures = [
  { icon: ShieldCheck, title: "Self-Verifying Contracts", desc: "Every protocol is audited and verified autonomously at the moment of deployment." },
  { icon: GitBranch, title: "Automated Liquidity Routing", desc: "AI parameters route liquidity to the optimal pools without manual intervention." },
  { icon: Cpu, title: "Zero-Latency Engine", desc: "A self-executing deployment engine ships your protocol on-chain in a single pass." },
];

const Eyebrow = ({ children, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 font-semibold"
  >
    <span className="w-6 h-px bg-cyan-400/60" />
    {children}
  </span>
);

export default function Home() {
  const [selected, setSelected] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <div className="relative min-h-screen w-full bg-black text-white overflow-x-hidden font-body">
      <DigitalBackground />
      <LogoWatermark />
      <Navbar />

      <main className="relative z-10">
        {/* ===================== HERO ===================== */}
        <section
          id="hero"
          data-testid="hero-section"
          className="relative min-h-screen flex items-center px-5 sm:px-8 pt-28 pb-20"
        >
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
                <Eyebrow testId="hero-eyebrow">Autonomous Intelligence · Decentralized Finance</Eyebrow>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={1}
                data-testid="hero-headline"
                className="font-heading font-black tracking-tight leading-[1.05] mt-6 text-4xl sm:text-5xl lg:text-6xl"
              >
                <span className="text-gradient-chrome">Welcome to</span>
                <br />
                <span className="text-gradient-accent">Autonomous Dex</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={2}
                data-testid="hero-story"
                className="mt-7 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
              >
                The world's first fully autonomous DeFi architect. Create, deploy, and scale
                liquidity, staking, and token protocols effortlessly — one platform, driven by
                self-executing AI parameters. No middlemen. No gatekeepers. Purely on-chain.
              </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <a
                  href="#capabilities"
                  data-testid="hero-explore-button"
                  className="btn-electric text-black font-bold text-sm px-7 py-3.5 rounded-full inline-flex items-center gap-2"
                >
                  Explore Capabilities <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="https://autonodex.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-deploy-button"
                  className="btn-ghost-electric border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full inline-flex items-center gap-2"
                >
                  Autono Dex
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===================== CAPABILITIES ===================== */}
        <section
          id="capabilities"
          data-testid="capabilities-section"
          className="relative px-5 sm:px-8 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <Eyebrow testId="capabilities-eyebrow">Creator Toolkit</Eyebrow>
              <h2 className="font-heading font-extrabold tracking-tight mt-5 text-2xl sm:text-3xl lg:text-4xl text-gradient-chrome">
                Everything you need to build on-chain
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
                Ten autonomous modules. One seamless platform. Launch and manage your entire DeFi
                stack without ever leaving Autonomous Dex.
              </p>
            </div>

            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {capabilities.map((c, i) => {
                const Icon = c.icon;
                return (
                  <ElectricCard
                    key={c.title}
                    delayClass={`delay-${(i % 4)}`}
                    className="p-6 group cursor-pointer"
                    testId={`capability-card-${i}`}
                    onClick={() => setSelected(c)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(c);
                      }
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-600/20 border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      <Icon className="w-6 h-6 text-cyan-400 group-hover:text-purple-400 transition-colors duration-500" />
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-lg sm:text-xl text-white">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500 group-hover:text-cyan-400 transition-colors duration-300">
                      Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </ElectricCard>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===================== CHAT WALL ===================== */}
        <ChatWall
          onRequestLogin={() => setAuthOpen(true)}
          onRequestProfile={() => navigate("/profile")}
        />

        {/* ===================== POWERED BY AI ===================== */}
        <section
          id="ai-core"
          data-testid="ai-section"
          className="relative px-5 sm:px-8 py-24 sm:py-32"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Eyebrow testId="ai-eyebrow">Powered by AI</Eyebrow>
                <h2 className="font-heading font-extrabold tracking-tight mt-5 text-2xl sm:text-3xl lg:text-4xl text-gradient-chrome">
                  Autonomous infrastructure, end to end
                </h2>
                <p className="mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed font-medium">
                  No human approval queue.
                  <br />
                  No unnecessary waiting periods.
                  <br />
                  No jumping between platforms.
                </p>
                <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-xl">
                  Autonomous Dex replaces the entire manual DeFi stack with a self-driving engine.
                  Describe your intent — the protocol is synthesized, verified, and deployed on-chain.
                </p>

                <div className="mt-9 space-y-4">
                  {aiFeatures.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <motion.div
                        key={f.title}
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        custom={i}
                        data-testid={`ai-feature-${i}`}
                        className="flex items-start gap-4"
                      >
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-black/60 border border-cyan-500/30 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-white text-base">{f.title}</h4>
                          <p className="text-sm text-slate-400 mt-1">{f.desc}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Mock AI terminal */}
              <ElectricCard className="p-6 sm:p-8" testId="ai-terminal" delayClass="delay-2">
                <div className="flex items-center gap-2 pb-4 border-b border-white/10">
                  <span className="w-3 h-3 rounded-full bg-red-500/70" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <span className="w-3 h-3 rounded-full bg-green-500/70" />
                  <span className="ml-3 font-mono text-xs text-slate-500">autonomous://deploy</span>
                </div>
                <div className="mt-5 font-mono text-xs sm:text-sm space-y-2.5 leading-relaxed">
                  <p className="text-slate-500">$ ad deploy --token "NOVA" --supply 1B</p>
                  <p className="text-cyan-400">› synthesizing contract parameters…</p>
                  <p className="text-cyan-400">› auto-verifying bytecode ✓</p>
                  <p className="text-purple-400">› routing initial liquidity ✓</p>
                  <p className="text-purple-400">› locking liquidity 365d ✓</p>
                  <p className="text-green-400">✓ NOVA deployed on-chain · 0.0s queue</p>
                  <p className="text-slate-300 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-cyan-400" /> protocol live
                    <span className="terminal-cursor text-cyan-400">▋</span>
                  </p>
                </div>
              </ElectricCard>
            </div>
          </div>
        </section>

        {/* ===================== OUTRO / CTA ===================== */}
        <section
          id="outro"
          data-testid="outro-section"
          className="relative px-5 sm:px-8 py-24 sm:py-36"
        >
          <div className="max-w-4xl mx-auto">
            <ElectricCard className="px-6 sm:px-14 py-16 sm:py-20 text-center" testId="outro-card">
              <Eyebrow testId="outro-eyebrow">The Autonomous Standard</Eyebrow>
              <h2 className="font-heading font-black tracking-tight mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
                <span className="text-gradient-chrome">Create it. Deploy it.</span>
                <br />
                <span className="text-gradient-accent">Manage it. On-chain.</span>
              </h2>
              <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl mx-auto">
                Autonomous Intelligence. Decentralized Finance. The future of building on-chain is
                self-executing — and it starts here.
              </p>
              <a
                href="https://autonodex.org/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="outro-cta-button"
                className="btn-electric text-black font-bold text-sm sm:text-base px-9 py-4 rounded-full inline-flex items-center gap-2 mt-10"
              >
                Enter Autonomous Dex <ArrowRight className="w-5 h-5" />
              </a>
            </ElectricCard>
          </div>
        </section>

        {/* ===================== REVIEWS MARQUEE ===================== */}
        <ReviewsMarquee />

        {/* ===================== FOOTER ===================== */}
        <footer
          data-testid="footer"
          className="relative z-10 border-t border-white/10 px-5 sm:px-8 py-10"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-heading font-extrabold text-sm tracking-widest text-white">
                AUTONOMOUS<span className="text-gradient-accent"> DEX</span>
              </span>
            </div>
            <a
              href="https://autonodex.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-autonodex-link"
              className="font-mono text-xs uppercase tracking-[0.18em] text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              autonodex.org
            </a>
            <p className="font-mono text-xs text-slate-500 tracking-wide">
              © 2026 Autonomous Dex · Decentralized &amp; Unstoppable.
            </p>
          </div>
        </footer>
      </main>

      {/* ===================== DETAIL MODAL ===================== */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="detail-overlay"
            data-testid="detail-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center p-5 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
              onClick={() => setSelected(null)}
              data-testid="detail-modal-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className={`electric-card relative z-10 w-full ${
                [
                  "swap",
                  "token",
                  "list",
                  "locker",
                  "airdrop",
                  "reviews",
                ].includes(selected.detail)
                  ? "max-w-2xl"
                  : "max-w-lg"
              } max-h-[85vh] overflow-hidden rounded-2xl bg-black/70 backdrop-blur-2xl border border-white/10 flex flex-col`}
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="detail-modal-close"
                aria-label="Close"
                className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-400/50 transition-colors duration-300"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="overflow-y-auto p-8 sm:p-10">
                {selected.detail === "swap" ? (
                  <SwapDetail />
                ) : selected.detail === "token" ? (
                  <TokenDetail />
                ) : selected.detail === "list" ? (
                  <ListDetail />
                ) : selected.detail === "locker" ? (
                  <LockerDetail />
                ) : selected.detail === "airdrop" ? (
                  <AirdropDetail />
                ) : selected.detail === "reviews" ? (
                  <ReviewsDetail
                    onRequestLogin={() => {
                      setSelected(null);
                      setAuthOpen(true);
                    }}
                  />
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/25 to-purple-600/25 border border-white/10 flex items-center justify-center">
                      {(() => {
                        const Icon = selected.icon;
                        return <Icon className="w-7 h-7 text-cyan-400" />;
                      })()}
                    </div>

                    <h3
                      data-testid="detail-modal-title"
                      className="mt-6 font-heading font-extrabold text-2xl sm:text-3xl text-gradient-chrome"
                    >
                      {selected.title}
                    </h3>
                    <p className="mt-4 text-base text-slate-300 leading-relaxed">{selected.long}</p>

                    {selected.links && (
                      <div className="mt-7 grid grid-cols-2 gap-3" data-testid="detail-modal-links">
                        {selected.links.map((l) => {
                          const LinkIcon = l.icon;
                          return (
                            <a
                              key={l.label}
                              href={l.href}
                              data-testid={`detail-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              className="btn-ghost-electric flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-200"
                            >
                              <LinkIcon className="w-4 h-4 text-cyan-400" />
                              {l.label}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
